import React from 'react'
import { IValueEditorProps } from '@app-types/general'
import { createLogger } from '@utils/logger'
import useValueEditor from '@hooks/useValueEditor'
import { sanitize } from '@utils/sanitize'
import RichMarkdownEditor from '@features/rich_markdown_editor/components/RichMarkdownEditor'

// Initialize logger
const log = createLogger('MarkdownEditor')
log.setLevel('info')

type ChangeObject = {
  name?: string
  value: string
}

type MarkdownEditorProps = Omit<IValueEditorProps, 'serializeValue' | 'parseValue'> & {
  serializeValue?: (value: string | null) => string | null
  parseValue?: (value: string | null) => string | null
}

const passthrough = (value: string | null): string | null => value
const defaultParseValueFn = passthrough
const defaultSerializeValueFn = passthrough

const MarkdownEditor: React.FC<MarkdownEditorProps> & { EDITOR_TYPE: string } = ({
  name,
  value = '',
  onChange,
  parseValue = defaultParseValueFn,
  serializeValue = defaultSerializeValueFn,
  ...restProps
}) => {
  const { localValue, handleLocalChange } = useValueEditor<string | null, string | null>({
    value,
    parseValue,
    serializeValue,
    onChange: onChange ? ({ value }) => onChange({ name, value }) : undefined,
  })

  const externalOnBlur = (restProps as any)?.onBlur

  const handleMarkdownEditorChange = (changeObj: ChangeObject) => {
    const sanitized = sanitize(changeObj.value)
    // Update both the commit controller state and the value editor's local state
    // so parent components (e.g., PopoverEdit) receive live onChange events and can track currentValue.
    handleLocalChange(sanitized)
  }

  const normalizedValue = localValue ?? ''
  return (
    <span className="content-editor content-editor--markdown markdown-editor">
      <RichMarkdownEditor
        {...restProps}
        value={normalizedValue}
        onChange={handleMarkdownEditorChange}
        onBlur={externalOnBlur}
        writeClassName="textarea"
      />
    </span>
  )
}

MarkdownEditor.EDITOR_TYPE = 'markdown'

export default MarkdownEditor
